import React, { useCallback, useEffect, useId, useRef } from "react";

type RequiredProps = {
    readonly setIsRendered: (value: boolean) => void;
};
type PrePostRendererProps = {
    readonly setIsRendered?: (value: boolean) => void;
};

/**
 * Hook that communicates to a parent ConditionalPrePostRenderer whether content should be rendered.
 * Must be used within a ConditionalPrePostRenderer component.
 *
 * E.g. `usePrePostRenderer(true)` indicates that the component is rendered and sets the state in
 * the parent so that the parent nows and can render other stuff in case this element is rendered.
 *
 * @param render - Boolean flag that determines whether rendering should occur
 * @throws Error if used outside of a ConditionalPrePostRenderer component
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   // Control rendering based on some condition
 *   usePrePostRenderer(isConditionMet);
 *   if (!isConditionMet) return null;
 *
 *   return <div>My content</div>;
 * }
 * ```
 */
const usePrePostRenderer = (render: boolean, doNotThrow = false): void => {
    const hookId = useId();
    const setIsRendered = React.useContext(
        ConditionalPrePostRenderChildContext,
    );
    if (!setIsRendered) {
        if (doNotThrow) return;
        throw new Error(
            "usePrePostRenderer must be used within a ConditionalPrePostRenderer",
        );
    }

    // It's ok to ignore the eslint rule here and use the hook conditionally
    // because the condition doesn't cause the hook to be skipped in some
    // render cycles. The hook will always be called in the same order.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        setIsRendered(render, hookId);
        return () => {
            setIsRendered(false, hookId);
        };
    }, [hookId, render, setIsRendered]);
    return;
};

/**
 * A component that conditionally renders items before and after its children,
 * based on whether any of its children are rendered.
 *
 * This component wraps each child in a provider that tracks rendering state.
 * The pre and post items will only be rendered when at least one child is rendered.
 *
 * @typeParam P - The type of props required by the child components, extending RequiredProps
 *
 * @example
 * ```tsx
 * <ConditionalPrePostRenderer
 *   preItem={<Header />}
 *   postItem={<Footer />}
 * >
 *   <ContentItem />
 * </ConditionalPrePostRenderer>
 * ```
 * IMPORTANT. Within the `ContentItem` component add `usePrePostRenderer(true/false)` to
 * report if the component is rendered or not, else the pre and post items will not be rendered
 * since this component won't know that is has been rendered.
 *
 * @param props - The component props
 * @param props.children - The child element(s) to render
 * @param props.preItem - Optional element to render before the children when any child is rendered
 * @param props.postItem - Optional element to render after the children when any child is rendered
 *
 * @returns A React fragment containing the conditionally rendered preItem, children, and postItem
 */
const ConditionalPrePostRenderer = <P extends RequiredProps>(props: {
    readonly children: React.ReactElement<P> | Array<React.ReactElement<P>>;
    readonly preItem?: React.ReactNode;
    readonly postItem?: React.ReactNode;
}) => {
    const values = useRef(new Map<string, boolean>());
    const [isRendered, _setIsRendered] = React.useState(false);
    const setIsRendered = useCallback(
        (value: boolean, hookId: string) => {
            values.current.set(hookId, value);
            const anyRendered = Array.from(values.current.values()).some(
                item => item,
            );
            _setIsRendered(anyRendered);
        },
        [_setIsRendered],
    );

    return (
        <ConditionalPrePostRenderChildProvider setIsRendered={setIsRendered}>
            {isRendered ? props.preItem : null}
            {props.children}
            {isRendered ? props.postItem : null}
        </ConditionalPrePostRenderChildProvider>
    );
};

const ConditionalPrePostRenderChildContext = React.createContext<
    ((value: boolean, hookId: string) => void) | undefined
>(undefined);

const ConditionalPrePostRenderChildProvider = (props: {
    readonly children: React.ReactNode;
    readonly setIsRendered: (value: boolean, hookId: string) => void;
}) => {
    return (
        <ConditionalPrePostRenderChildContext.Provider
            value={props.setIsRendered}>
            {props.children}
        </ConditionalPrePostRenderChildContext.Provider>
    );
};

export { ConditionalPrePostRenderer, usePrePostRenderer };
export type { PrePostRendererProps };
